import { MdDownload, MdQuestionMark } from "react-icons/md";
import "./index.css";
import { FaFilePdf } from "react-icons/fa";
import { IoIosDownload, IoIosSearch, IoMdClose } from "react-icons/io";
import { useState, useMemo } from "react";

const isExplicitImageUrl = (url) =>
  url &&
  (/\.(png|jpe?g|gif|webp)(\?|#|$)/i.test(url) ||
    url.includes(".png") ||
    url.includes(".jpg") ||
    url.includes(".jpeg"));

const isPdfUrl = (url) => url && url.includes(".pdf");

/** S3 keys often omit extensions (e.g. FILE_timestamp_...); try <img> unless clearly non-image. */
const shouldRenderAsImage = (url) => {
  if (!url || isPdfUrl(url)) return false;
  if (isExplicitImageUrl(url)) return true;
  try {
    const basename = (url.split("?")[0].split("/").pop() || "").toLowerCase();
    if (/\.(pdf|zip|doc|docx|xls|xlsx|txt|csv)$/i.test(basename)) return false;
    return true;
  } catch {
    return false;
  }
};

const getFileTypeLabelFromUrl = (url) => {
  if (!url) return "?";
  if (isPdfUrl(url)) return "PDF";
  if (url.includes(".png")) return "PNG";
  if (url.includes(".jpg") || url.includes(".jpeg")) return "JPG";
  return "FILE";
};

const getFileNameFromUrl = (url) => {
  if (!url) return "";
  try {
    const withoutQuery = url.split("?")[0];
    const segments = withoutQuery.split("/");
    const last = segments[segments.length - 1] || "";
    return decodeURIComponent(last);
  } catch {
    return "";
  }
};

const getFileNameFromKey = (key) => {
  if (!key || typeof key !== "string") return "";
  try {
    return decodeURIComponent(key);
  } catch {
    return key;
  }
};

const FileViewerAWS = ({ signedUrls = [], fileKeys }) => {
  const [expandedImage, setExpandedImage] = useState(null);
  const [failedImageIndices, setFailedImageIndices] = useState(() => new Set());

  const items = useMemo(() => {
    if (fileKeys && fileKeys.length > 0) {
      return fileKeys.map((key, i) => ({ key: key ?? i, url: signedUrls[i] ?? null }));
    }
    return signedUrls.map((url, i) => ({ key: i, url }));
  }, [signedUrls, fileKeys]);

  const handleImageClick = (url) => {
    setExpandedImage(url);
  };

  const handleCloseImage = () => {
    setExpandedImage(null);
  };

  const handleImageError = (index) => {
    setFailedImageIndices((prev) => new Set(prev).add(index));
  };

  const renderPlaceholder = (item, index, fileName) => (
    <div
      className="file-viewer-preview file-viewer-preview-generic file-viewer-placeholder"
      aria-label={fileName || "No disponible"}
    >
      <MdQuestionMark className="file-viewer-generic-icon file-viewer-placeholder-icon" />
    </div>
  );

  return (
    <>
      <div className="file-viewer-list">
        {items.map((item, index) => {
          const { url, key } = item;
          const isFailedImage = failedImageIndices.has(index);
          const showPlaceholder = !url || url === "" || isFailedImage;
          const fileName = getFileNameFromUrl(url) || getFileNameFromKey(typeof key === "string" ? key : null);
          const fileType = getFileTypeLabelFromUrl(url);

          return (
            <div key={`${key}-${index}`} className="file-viewer-item">
              {showPlaceholder ? (
                renderPlaceholder(item, index, fileName)
              ) : shouldRenderAsImage(url) ? (
                <button
                  type="button"
                  className="file-viewer-preview"
                  onClick={() => handleImageClick(url)}
                  aria-label={fileName || "View image"}
                >
                  <img
                    src={url}
                    alt={fileName || `File ${index + 1}`}
                    className="file-viewer-img"
                    onError={() => handleImageError(index)}
                  />
                  <div className="file-viewer-overlay">
                    <IoIosSearch size={26} />
                  </div>
                </button>
              ) : isPdfUrl(url) ? (
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="file-viewer-preview file-viewer-preview-pdf"
                  aria-label={fileName || "Open PDF"}
                >
                  <FaFilePdf className="file-viewer-pdf-icon" />
                  <div className="file-viewer-overlay">
                    <IoIosDownload size={26} />
                  </div>
                </a>
              ) : (
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="file-viewer-preview file-viewer-preview-generic"
                  aria-label={fileName || "Download file"}
                >
                  <MdQuestionMark className="file-viewer-generic-icon" />
                  <div className="file-viewer-overlay">
                    <IoIosDownload size={26} />
                  </div>
                </a>
              )}

              {(fileName || fileType) && (
                <div className="file-viewer-meta">
                  {fileName && (
                    <div
                      className="file-viewer-name"
                      title={fileName}
                    >
                      {fileName}
                    </div>
                  )}
                  <div className="file-viewer-type">
                    {fileType}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {expandedImage && (
        <div className="file-viewer-modal-overlay">
          <div className="file-viewer-modal-content">
            <img
              src={expandedImage}
              alt="Expanded"
              className="file-viewer-expanded-image"
            />

            <div className="file-viewer-modal-actions">
              <button
                type="button"
                className="file-viewer-btn"
                onClick={handleCloseImage}
                aria-label="Close"
              >
                <IoMdClose size={22} />
              </button>

              <a
                href={expandedImage}
                target="_blank"
                rel="noopener noreferrer"
                className="file-viewer-btn"
                aria-label="Download"
              >
                <MdDownload size={22} />
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FileViewerAWS;
